/**
 * 6-digit OTP input — one cell per digit, auto-advance on type,
 * paste-supported.
 *
 * BEHAVIOUR (unchanged — this is a visual + a11y rebuild, and every one of
 * these rules is covered by VerifyEmailScreen.test.tsx / ResetPasswordScreen
 * .test.tsx):
 *   - Six text inputs (`inputMode="numeric"`) rendered horizontally.
 *   - Typing in cell N auto-advances focus to cell N+1.
 *   - Backspace from an empty cell pulls focus back to N-1; Backspace on a
 *     populated cell clears it in place so a typo can be retyped where it is.
 *   - Pasting a 6-digit blob fills every cell AND focuses the last one.
 *   - On error the parent clears `value`; focus returns to cell 0.
 *   - `autoComplete="one-time-code"` on cell 0 so iOS/Android offer the SMS or
 *     mail code from the notification shade. This is the single highest-value
 *     line in the file for a phone user and it stays.
 *
 * WHAT CHANGED, and why:
 *
 * The cells were `rounded-md border bg-background` — six little text boxes, the
 * same shape as every other input on the screen, so nothing said "this is a
 * code you were handed". In the counter world an OTP is the stub of a slip: a
 * figure, printed, in the instrument face. So the cells now sit in a RECESSED
 * WELL (`--g-sunken`) divided by drawn rules, and each digit is set in
 * `.g-data` (IBM Plex Mono, tabular, with its dotted zero) — the one place in
 * auth where monospace is honest, because the content genuinely IS a
 * measurement and a marked zero genuinely does disambiguate 0 from O when a
 * user is copying six characters off a screen.
 *
 * The cells are 44px wide × 52px tall: the tap-target floor per cell, and six
 * of them plus gaps still fit inside a 390px viewport with the page gutters.
 *
 * `focusFirst()` is exposed imperatively because the reset-password form needs
 * to move focus here when submit fails on the CODE field. The cells are
 * controlled, not registered with react-hook-form, so RHF's own
 * `shouldFocusError` cannot reach them — without this handle a user whose only
 * error is the code gets an error message and no focus move.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

export interface OtpInputHandle {
  /** Move focus to the first cell. Used on submit-time validation failure. */
  focusFirst: () => void;
}

export interface OtpInputProps {
  /** Controlled value — exactly 0..6 digits, no other characters. */
  value: string;
  /** Called with the new value on each cell update or paste. */
  onChange: (next: string) => void;
  /** Called when the user has filled all 6 cells. */
  onComplete?: (full: string) => void;
  /** Accessible label rendered visually above the cell row. */
  label: string;
  /** Disable the entire row (during submit). */
  disabled?: boolean;
  /** Set on each input via `aria-invalid` so screen readers announce. */
  invalid?: boolean;
  /** Optional id of the error region for `aria-describedby`. */
  errorId?: string;
  /** Auto-focus the first cell on mount. Default: true. */
  autoFocus?: boolean;
  /**
   * test id, attached to each cell as `${testId}-${index}` so RTL can
   * `getByTestId('otp-cell-0')`-style query.
   */
  testId?: string;
  /** Total number of cells. Story body says 6; left configurable for tests. */
  length?: number;
  /**
   * Draw the required marker on the label, matching the slip rows this sits
   * beside. Off by default so the standalone call sites (seller registration)
   * are unchanged; /reset-password turns it on because a form where two of
   * three rows are marked and the third is not reads as "this one is optional".
   */
  required?: boolean;
}

const ONLY_DIGITS = /[^0-9]/g;

export const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>(
  function OtpInput(
    {
      value,
      onChange,
      onComplete,
      label,
      disabled = false,
      invalid = false,
      errorId,
      autoFocus = true,
      testId = "otp-cell",
      length = 6,
      required = false,
    },
    ref,
  ): ReactElement {
    const { t } = useTranslation("auth");
    const inputs = useRef<Array<HTMLInputElement | null>>([]);
    const reactId = useId();
    const wrapperId = "otp-input-" + reactId;

    useImperativeHandle(
      ref,
      () => ({
        focusFirst: (): void => {
          inputs.current[0]?.focus();
        },
      }),
      [],
    );

    // Auto-focus the first cell on mount. AC #5 also requires the
    // first cell to regain focus after an error; the parent clears
    // `value` to "" on error and the effect below re-focuses cell 0.
    useEffect(() => {
      if (!autoFocus) return;
      inputs.current[0]?.focus();
    }, [autoFocus]);

    // If the parent resets value to empty AFTER the user typed (the
    // wrong-code error path per AC #5), pull focus back to cell 0.
    useEffect(() => {
      if (value === "" && autoFocus) {
        inputs.current[0]?.focus();
      }
    }, [value, autoFocus]);

    const setDigitAt = useCallback(
      (index: number, digit: string) => {
        // Build the new full string by slicing existing value around `index`.
        const arr = value.split("");
        // Pad with empty so set-at-end works even if value was shorter.
        while (arr.length < length) arr.push("");
        arr[index] = digit;
        const next = arr.join("").slice(0, length);
        onChange(next);
        if (next.length === length && next.replace(ONLY_DIGITS, "") === next) {
          onComplete?.(next);
        }
      },
      [value, onChange, onComplete, length],
    );

    const handleChange = useCallback(
      (index: number, raw: string) => {
        const sanitized = raw.replace(ONLY_DIGITS, "");
        if (sanitized.length === 0) {
          setDigitAt(index, "");
          return;
        }
        // If the user typed (or pasted) more than one digit into a cell,
        // spread the extras across the following cells.
        const arr = value.split("");
        while (arr.length < length) arr.push("");
        let cursor = index;
        for (const ch of sanitized) {
          if (cursor >= length) break;
          arr[cursor] = ch;
          cursor++;
        }
        const next = arr.join("").slice(0, length);
        onChange(next);
        // Advance focus to the last filled cell + 1 (clamped).
        const nextFocus = Math.min(cursor, length - 1);
        inputs.current[nextFocus]?.focus();
        if (next.length === length && next.replace(ONLY_DIGITS, "") === next) {
          onComplete?.(next);
        }
      },
      [value, onChange, onComplete, length, setDigitAt],
    );

    const handleKeyDown = useCallback(
      (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace") {
          if ((value[index] ?? "") === "") {
            // Empty cell: move focus back and clear the previous cell too.
            const prev = index - 1;
            if (prev >= 0) {
              inputs.current[prev]?.focus();
              // Don't auto-clear the previous cell — match iOS OTP UX
              // where one Backspace = one move; two = also clear.
            }
            // No state change here.
            return;
          }
          // Non-empty cell: clear it in place (no focus move).
          event.preventDefault();
          setDigitAt(index, "");
          return;
        }
        if (event.key === "ArrowLeft" && index > 0) {
          event.preventDefault();
          inputs.current[index - 1]?.focus();
          return;
        }
        if (event.key === "ArrowRight" && index < length - 1) {
          event.preventDefault();
          inputs.current[index + 1]?.focus();
          return;
        }
      },
      [value, setDigitAt, length],
    );

    const handlePaste = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = event.clipboardData
          .getData("text")
          .replace(ONLY_DIGITS, "")
          .slice(0, length);
        if (pasted.length === 0) return;
        event.preventDefault();
        onChange(pasted);
        // Focus the last filled cell (or the last cell if fully filled).
        const last = Math.min(pasted.length - 1, length - 1);
        inputs.current[last]?.focus();
        if (pasted.length === length) {
          onComplete?.(pasted);
        }
      },
      [length, onChange, onComplete],
    );

    return (
      <div className="flex flex-col gap-2" data-testid={wrapperId}>
        <label
          className="text-[0.6875rem] font-bold uppercase leading-tight tracking-[0.09em] text-[var(--g-ink-2)]"
          htmlFor={wrapperId + "-cell-0"}
        >
          {label}
          {required ? (
            <>
              <span aria-hidden="true" className="text-[var(--g-accent)]">
                {" *"}
              </span>
              <span className="sr-only">{" " + t("common.requiredField")}</span>
            </>
          ) : null}
        </label>
        {/* The recessed well. The cells are divided by drawn rules rather than
            floated apart, so six digits read as ONE printed figure. */}
        <div
          className={
            "flex overflow-hidden rounded-[var(--g-radius)] border bg-[var(--g-sunken)] " +
            (invalid ? "border-[var(--g-error)]" : "border-[var(--g-rule)]")
          }
          role="group"
          aria-label={label}
        >
          {Array.from({ length }).map((_, i) => {
            const cellId = wrapperId + "-cell-" + i;
            return (
              <input
                key={i}
                id={cellId}
                ref={(el) => {
                  inputs.current[i] = el;
                }}
                data-testid={testId + "-" + i}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={1}
                aria-invalid={invalid || undefined}
                aria-describedby={errorId}
                aria-label={t("common.otpCellA11y", {
                  index: i + 1,
                  total: length,
                })}
                disabled={disabled}
                value={value[i] ?? ""}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                className={
                  "g-data h-[3.25rem] min-w-[2.75rem] flex-1 bg-transparent text-center " +
                  "text-xl font-medium text-[var(--g-figure)] outline-none " +
                  "transition-colors focus:bg-[var(--g-surface)] " +
                  "focus-visible:shadow-[inset_0_-2px_0_0_var(--g-ink)] " +
                  "disabled:cursor-not-allowed disabled:opacity-60 " +
                  (i > 0
                    ? "border-y-0 border-r-0 border-l border-[var(--g-rule)]"
                    : "border-0")
                }
              />
            );
          })}
        </div>
      </div>
    );
  },
);

export default OtpInput;
